<template>
  <div class="guide-embedded-signing">
    <h2>What is embedded signing?</h2>
    <p>
      In a standard email-based flow, the signer receives an email containing a
      link to the signing ceremony. They click the link, sign the document in
      the eSign portal, and return to whatever they were doing when the email
      arrived.
    </p>
    <p>
      Embedded signing changes who initiates that flow. Instead of the system
      sending an email, your application generates a signing URL
      programmatically and directs the signer to it at the right moment in your
      workflow. This fits scenarios where the signer is already interacting
      with your application and you want signing to happen immediately rather
      than asynchronously via email.
    </p>

    <q-separator class="q-my-lg" />

    <h2>When to use embedded vs. email-based</h2>
    <p>
      The choice between embedded and email-based signing depends on your relationship with the
      signer and the context in which signing occurs.
    </p>
    <table class="q-table q-mb-md" style="width: 100%; border-collapse: collapse;">
      <thead>
        <tr>
          <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Scenario</th>
          <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Approach</th>
          <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Why</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Signer is already in your app</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Embedded</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Immediate signing, no email delay</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Signer is external (customer, vendor)</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Email-based</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Asynchronous, no app needed</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Real-time signing flow (for example, checkout)</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Embedded</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Immediate completion required</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Multi-party contract sent to many people</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Email-based</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Each signer signs at their convenience</td>
        </tr>
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Point-of-sale or kiosk scenarios</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Embedded</td>
          <td style="padding: 8px; border-bottom: 1px solid #e0e0e0;">Controlled environment, no email</td>
        </tr>
      </tbody>
    </table>

    <q-separator class="q-my-lg" />

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      Embedded signing is inherently a programmatic feature. Your application
      generates a signing URL and redirects the user to it (or embeds an iframe).
      Everything below is API-focused.
    </p>

    <h3>SDK Quickstart (recommended for browser apps)</h3>
    <p>
      The official <code>@esignlaunchpad/sdk</code> wraps the iframe lifecycle,
      handles postMessage events, and supports a Focused View for minimal-chrome
      embedding. It's the fastest path to a working integration. If you're a
      server-rendered app or building from a non-browser environment, skip to
      Step 1 below for the raw API surface.
    </p>

    <q-banner class="bg-amber-1 text-amber-9 q-mb-md" rounded>
      <template v-slot:avatar>
        <q-icon name="aspect_ratio" color="amber-9" />
      </template>
      <strong>Pick the right display format for your iframe size.</strong>
      For embedded (iframe) integrations, <strong>always pass
      <code>displayFormat: 'focused'</code></strong>. Focused View strips the
      side rail and most chrome so the document gets the full iframe width;
      it's designed for embedding in containers as small as ~640&times;640.
      Classic View (the default) is built for full-browser-window standalone
      sessions and needs ~1200&times;800 to render without clipping - if
      you embed Classic in a smaller iframe the document gets cut off and
      action overlays land on top of fields. Pair Focused View with the
      <code>autoResize</code> option (below) to let the iframe grow to fit
      its content.
    </q-banner>

    <h4>Script tag (UMD)</h4>
    <p>
      The simplest setup. Pin to a content-hashed filename plus an SRI integrity
      hash so browser cache poisoning can't substitute a tampered SDK build.
      Get the latest filename + SRI from
      <code>https://www.esignlaunchpad.com/sdk/v1/latest.json</code>.
    </p>
<pre><code>&lt;div id="agreement"&gt;&lt;/div&gt;

&lt;script src="https://www.esignlaunchpad.com/sdk/v1/esignlaunchpad-{hash}.js"
        integrity="sha384-..."
        crossorigin="anonymous"&gt;&lt;/script&gt;
&lt;script&gt;
  // signingUrl is the URL your server minted via
  // POST /api/v1/packages/{packageId}/signers/{signerId}/signing-url
  const handle = window.eSignLaunchpad.signing({
    url: signingUrl,
    displayFormat: 'focused',  // recommended for iframe embedding
    autoResize: true,          // grows the iframe to fit ceremony content
    style: { primaryButton: { backgroundColor: '#1e40af', color: '#ffffff' } }
  });

  handle
    .mount('#agreement')
    .on('ready', () =&gt; console.log('iframe loaded'))
    .on('sessionEnd', payload =&gt; {
      // payload.event matches the SigningCeremonyEvent vocabulary
      // (signing_complete, declined, ttl_expired, ...)
      handle.unmount();
      // Render your own confirmation UI in the parent app here.
      console.log('signing ended:', payload.event, payload.signerId);
    });
&lt;/script&gt;</code></pre>

    <h4>ESM (Vite, webpack, etc.)</h4>
<pre><code>import { signing, DisplayFormat } from '@esignlaunchpad/sdk';

const handle = signing({
  url: signingUrl,
  displayFormat: DisplayFormat.Focused,
  autoResize: true,
});

handle
  .mount('#agreement')
  .on('sessionEnd', payload =&gt; {
    handle.unmount();
    // Render your own confirmation UI here.
  });</code></pre>

    <h4>Public surface</h4>
    <ul>
      <li><code>signing(options)</code> - create a session</li>
      <li><code>.mount(selector | element)</code> - drop the iframe into the page</li>
      <li><code>.on('ready', handler)</code> - iframe loaded + interactive</li>
      <li><code>.on('sessionEnd', payload =&gt; ...)</code> - terminal state with <code>{ event, signerId, reason }</code></li>
      <li><code>.unmount()</code> - clean teardown (removes iframe + listeners)</li>
      <li><code>DisplayFormat.Classic</code> | <code>DisplayFormat.Focused</code> - full-window vs iframe-friendly chrome</li>
      <li><code>autoResize: true</code> - iframe auto-grows to fit ceremony content height, clamped between <code>autoResizeMinHeight</code> (default 400) and <code>autoResizeMaxHeight</code> (default 2400). See <a href="#sizing-the-iframe">Sizing the iframe</a> below for how to shape the host container so growth works without overflow.</li>
      <li><code>SigningCeremonyEvent</code> - string constants for every terminal-state event name</li>
    </ul>

    <q-banner class="bg-blue-1 text-blue-9 q-mb-md" rounded>
      <template v-slot:avatar>
        <q-icon name="security" color="blue-9" />
      </template>
      <strong>Why postMessage events AND the returnUrl redirect?</strong> Both fire
      at session end. postMessage is the in-app signal (your SDK handler gets
      <code>sessionEnd</code> immediately). The returnUrl redirect is the
      authoritative fallback (works even when postMessage delivery is interrupted
      by the navigation, or when a customer doesn't include the SDK at all).
      For mission-critical state, also subscribe to webhooks - they fire
      server-side and survive client-side failures.
    </q-banner>

    <q-separator class="q-my-md" />

    <h3 id="sizing-the-iframe">Sizing the iframe</h3>
    <p>
      With <code>autoResize: true</code>, the SDK measures the ceremony's
      content height and writes <code>iframe.style.height</code> on every
      meaningful state change (consent &rarr; auth &rarr; signing). Your job
      on the host page is to make sure the iframe's parent element grows with
      the iframe rather than constraining it.
    </p>

    <p><strong>Working example.</strong> Drop this into your page and adjust
    <code>min-height</code> to taste:</p>
<pre><code>&lt;div id="agreement"&gt;&lt;/div&gt;

&lt;style&gt;
  /* The host container: a sensible initial height, free to grow. */
  #agreement &#123;
    width: 100%;
    min-height: 720px;
    height: auto;
  &#125;
  /* Block layout removes the 4px inline-element baseline gap that some
     browsers leave below an iframe. */
  #agreement iframe &#123;
    display: block;
    width: 100%;
    border: 0;
  &#125;
&lt;/style&gt;

&lt;script&gt;
  window.eSignLaunchpad.signing(&#123;
    url: signingUrl,
    displayFormat: 'focused',
    autoResize: true,
    // Match the host's min-height so the iframe never shrinks below your
    // intended viewport on a short auth screen.
    autoResizeMinHeight: 720,
  &#125;).mount('#agreement');
&lt;/script&gt;</code></pre>

    <p><strong>The two rules that matter</strong></p>
    <ul>
      <li>
        <strong>Use <code>min-height</code>, not <code>height</code>, on the
        host container.</strong> A fixed <code>height: 720px</code> caps the
        host while the SDK grows the iframe past it - you get the iframe
        visually spilling out of (or being clipped by) its parent. Use
        <code>min-height</code> for the floor and let <code>height: auto</code>
        track the iframe's measured size.
      </li>
      <li>
        <strong>Pass <code>autoResizeMinHeight</code> matching the host's
        <code>min-height</code>.</strong> The SDK's default floor is 400px.
        On lighter screens (consent acceptance, a 4-question KBA form) the
        measured content can be shorter than your host's min-height; without
        this option the iframe shrinks to its content and leaves a visible
        gap inside the host box. Passing the same value to both pins them
        together.
      </li>
    </ul>

    <p><strong>What to avoid</strong></p>
    <ul>
      <li>
        <strong>Wrapping the iframe in a container with <code>overflow: hidden</code>
        and a fixed height.</strong> Same outcome as a fixed-height host -
        autoResize grows the iframe, the wrapper clips it, signing controls
        disappear below the cut.
      </li>
      <li>
        <strong>Pinning ancestors to <code>height: 100vh</code>.</strong>
        Inside an iframe, <code>100vh</code> equals the iframe's current
        height - autoResize then pushes the iframe taller, ancestors pinned
        to <code>100vh</code> grow with it, and the measurement fights the
        clamp. Inside the iframe we already neutralize this for our own
        layout; mirror the same discipline in the chain of elements
        between your iframe and the document root.
      </li>
      <li>
        <strong>Setting <code>iframe.style.height</code> from your own
        code.</strong> The SDK owns the iframe's inline height. CSS rules
        with <code>!important</code> on the iframe's height override the SDK
        and break autoResize.
      </li>
      <li>
        <strong>Putting the iframe inside a flex/grid track without explicit
        sizing rules for the cross-axis.</strong> Some flex/grid configurations
        try to size the iframe to track height, which surprises autoResize.
        If you must use flex/grid, give the iframe's track
        <code>align-self: start</code> or set explicit row sizing on the grid.
      </li>
    </ul>

    <p>
      For Classic display format (full-window, non-embedded), none of the
      above applies - Classic owns its own viewport. The sizing concerns
      are specific to <code>displayFormat: 'focused'</code> with
      <code>autoResize: true</code>.
    </p>

    <q-separator class="q-my-md" />

    <h3>The <code>clientUserId</code> pattern</h3>
    <p>
      The <code>clientUserId</code> field is the single mechanism that switches
      a signer from email-based to embedded mode. Understanding this pattern is
      the key to implementing embedded signing correctly.
    </p>

    <q-banner class="bg-amber-1 text-amber-10 q-mb-md" rounded>
      <template v-slot:avatar>
        <q-icon name="lock" color="amber-10" />
      </template>
      <strong>Subscription feature gate.</strong> Embedded signing must be enabled on your
      subscription plan. Attempting to add a signer with <code>clientUserId</code>, or
      calling the signing-URL endpoint, returns <code>403 Forbidden</code> with the error
      code <code>FEATURE_NOT_AVAILABLE</code> if the feature is not enabled on your plan.
    </q-banner>

    <h3>Configure your embedding origins (required before going live)</h3>
    <p>
      Before your iframe will render, your tenant admin must add your application's
      origin to the allowlist at
      <a href="/Settings/Embedding"><strong>Settings &rarr; Embedded Signing</strong></a>.
      Embedded signing is fail-closed by default: a tenant with no origins configured
      cannot embed signing ceremonies in any iframe - the browser blocks the load via
      <code>frame-ancestors 'none'</code>.
    </p>
    <p>The simplest production setup:</p>
    <ol>
      <li>Open <a href="/Settings/Embedding">Settings &rarr; Embedded Signing</a> as a Tenant Admin</li>
      <li>Add the origin of every web app that will embed your signing pages
          (e.g. <code>https://app.your-company.com</code>, plus any staging origins)</li>
      <li>Save</li>
    </ol>
    <q-banner class="bg-blue-1 text-blue-9 q-mb-md" rounded>
      <template v-slot:avatar>
        <q-icon name="info" color="blue-9" />
      </template>
      <strong>For per-request control,</strong> you can override the tenant default on
      a per-signing-URL basis by passing <code>allowedOrigins</code> in the request body
      to <code>POST /api/v1/packages/{id}/signers/{id}/signing-url</code> (covered in
      Step 2 below). Per-request origins are encoded into the HMAC-signed token itself
      and cannot be widened by re-minting.
    </q-banner>

    <h3>Step 1. Create and send the package in a single call</h3>
    <p>
      The <code>create-and-send</code> endpoint atomically creates the
      package, attaches your documents, adds your signers, and sends it for
      signing in one request. Mark a signer as embedded by setting its
      <code>clientUserId</code> field to your internal identifier for that
      person.
    </p>

    <GuideCodeTabs :samples="samples.createAndSend" />

    <p>Setting <code>clientUserId</code> on a signer tells the system two things:</p>
    <ul>
      <li>This signer is managed by your application (embedded mode).</li>
      <li>
        <strong>Suppress the email notification.</strong> Your app is
        responsible for directing this signer to the signing ceremony, so the
        system does not send them an email.
      </li>
    </ul>

    <q-banner class="bg-orange-1 text-orange-9 q-mb-md" rounded>
      <strong>Important:</strong> If you omit <code>clientUserId</code>, the
      signer is treated as email-based and receives a notification email when
      the package is sent. The two modes are mutually exclusive per signer.
      You can mix embedded and email-based signers within the same package.
    </q-banner>

    <q-banner class="bg-blue-1 text-blue-9 q-mb-md" rounded>
      <template v-slot:avatar>
        <q-icon name="tune" color="blue-9" />
      </template>
      <strong>Need finer control?</strong> If you want to build the package
      incrementally (upload documents one at a time, add signers across
      multiple steps, save a draft and send later, or reuse a template), use
      the granular endpoints instead:
      <code>POST /api/v1/packages</code>,
      <code>POST /api/v1/packages/{id}/documents</code>,
      <code>POST /api/v1/packages/{id}/signers</code>, then
      <code>POST /api/v1/packages/{id}/send</code>. The same
      <code>clientUserId</code> rule applies on the signer endpoint. For
      template-based creation, see
      <code>POST /api/v1/templates/{id}/use</code> in the
      <a href="/Docs/ApiReference">API Reference</a>.
    </q-banner>

    <h3>Step 2. Generate a signing URL</h3>
    <p>
      After the package is sent, generate a short-lived, single-use signing
      URL for the embedded signer. This URL is what you redirect (or iframe)
      the user to when it is time for them to sign.
    </p>

    <GuideCodeTabs :samples="samples.getSigningUrl" />

    <ul>
      <li><code>expiresInMinutes</code>. Optional. Default is 5 minutes, maximum 60 minutes. Values outside that range are clamped.</li>
      <li><code>returnUrl</code>. Optional. The signer is redirected here after they complete, decline, or time out of the signing ceremony. Omit to use the default platform confirmation page. The redirect carries <code>?event=&lt;event&gt;&amp;signerId=&lt;guid&gt;</code> so your app can branch on the outcome.</li>
      <li><code>allowedOrigins</code>. Optional. Per-URL CSP <code>frame-ancestors</code> override. Up to 25 entries; each <code>https://host[:port]</code> or <code>https://*.parent.example</code>. Encoded into the HMAC-signed token, so re-mints cannot widen previously-issued URLs.</li>
      <li><code>pingUrl</code>. Optional. See "Keep the user's parent-app session alive" below.</li>
      <li><code>pingFrequencySeconds</code>. Optional. Ping interval in seconds, clamped to <code>[60, 600]</code>; default 300 when <code>pingUrl</code> is set.</li>
      <li><code>externalAuthMetadata</code>. Optional. See "Pass external authentication metadata" below.</li>
    </ul>
    <p>The response contains the signing URL and its expiration time:</p>

    <GuideCodeTabs :samples="samples.signingUrlResponse" />

    <p>
      Each URL is single-use. Once the signer loads it (or it expires), you
      must generate a new one. Calling this endpoint requires the
      <code>sign.package.send</code> scope.
    </p>

    <h3>Step 2a. Keep the user's parent-app session alive (optional)</h3>
    <p>
      Customer apps that embed the signing iframe usually have their own
      inactivity timeout. While the user spends 5+ minutes signing, the
      parent-app session can expire and the post-signing redirect lands them
      on a logged-out page. The <code>pingUrl</code> option fixes this: the
      embedded ceremony fetches the URL on a timer, with the user's session
      cookie attached, so the parent app's session-touch endpoint resets its
      TTL while signing is in progress.
    </p>
    <ul>
      <li>
        <code>pingUrl</code> must be HTTPS in production. <code>http://</code>
        is allowed only for <code>localhost</code>, <code>127.0.0.1</code>,
        or <code>[::1]</code> during local development - same rule as the
        framing allowlist validator. Its <strong>origin must match one of
        the resolved <code>allowedOrigins</code></strong> for this signing
        URL (per-token override > tenant default). If no allowlist applies,
        the request is rejected with <code>INVALID_PING_URL_ORIGIN</code>.
        This prevents the embedded ceremony from being used as a CSRF
        beachhead against arbitrary endpoints.
      </li>
      <li>
        The fetch uses <code>credentials: 'include'</code> and
        <code>mode: 'no-cors'</code>. The user's session cookie rides along;
        the iframe ignores the response (it cannot read it under no-cors).
        Your endpoint just needs to refresh the user's session and respond
        with anything (200 OK is fine).
      </li>
      <li>
        Failures are logged client-side and ignored - pinging is best-effort,
        not load-bearing.
      </li>
    </ul>

    <q-banner class="bg-amber-1 text-amber-9 q-mt-md" rounded>
      <strong>Documented failure mode:</strong> because <code>mode: 'no-cors'</code>
      makes the response opaque, the iframe <em>cannot</em> detect that the
      parent-app session has actually expired (e.g. <code>pingUrl</code>
      returns 401). The user keeps signing into a dead session; on completion
      the <code>returnUrl</code> redirect bounces them to the parent app's
      login screen. Recommended pattern: when your parent app detects that a
      user returned post-signing without a valid session, redirect them to a
      "your signing was completed but you were logged out - please log back
      in to continue" page, optionally with a status check against
      <code>GET /api/v1/packages/{packageId}</code> to confirm signing
      actually succeeded before showing a confirmation message.
    </q-banner>

    <h3>Step 2b. Pass external authentication metadata (optional, enterprise SSO)</h3>
    <p>
      If your application has already authenticated the signer (typically via
      SAML or OIDC SSO) before they reach the signing step, you can record
      that authentication on the Certificate of Completion via
      <code>externalAuthMetadata</code>. This strengthens the evidentiary
      record for compliance-driven workflows: HR (benefits acks, offer
      letters), healthcare (HIPAA documentation), financial onboarding,
      legal-tech engagement letters, and similar use-cases where regulators
      or auditors ask "was the user authenticated when they signed, and how?"
    </p>

    <GuideCodeTabs :samples="samples.signingUrlWithExternalAuth" />

    <ul>
      <li>
        <code>method</code> is required, free-form text up to 200 chars
        (e.g. <code>"SAML SSO via Okta"</code>, <code>"OIDC via Auth0"</code>).
      </li>
      <li>
        <code>instant</code> is required, ISO 8601 UTC. Bounded server-side to
        <code>[package.CreatedOn, UtcNow + 60s]</code> - non-configurable. Out
        of bounds returns <code>INVALID_EXTERNAL_AUTH_INSTANT</code>.
      </li>
      <li>
        <code>assertionId</code> and <code>securityDomain</code> are optional,
        each up to 200 chars. Use these to correlate the COC entry with your
        IdP's logs (typically the SAML AssertionID and the IdP entity ID).
      </li>
    </ul>

    <p>
      The metadata is mutable until the moment of signature. At
      <code>SignerStatus → Completed</code>, eSign Launchpad snapshots the
      asserted values into immutable evidence columns and the COC reads from
      that snapshot only. Re-mints between mint and signing overwrite the
      mutable copy and write a fresh audit-trail entry; the audit blob
      preserves the complete chain of every assertion you've made.
    </p>

    <h4>What appears on the Certificate of Completion</h4>
    <p>
      Below the signer's standard authentication record, the COC adds an
      "External authentication" section with intentional liability-limiting
      wording:
    </p>

    <q-card flat bordered class="q-pa-md q-mt-sm bg-grey-1">
      <p class="text-caption text-grey-7 q-mb-xs">
        External authentication (asserted by integrator, not independently
        verified by eSign Launchpad):
      </p>
      <p class="text-caption q-mb-xs">Asserted method: SAML SSO via Okta</p>
      <p class="text-caption q-mb-xs">Asserted authentication time: 2026-05-06 09:00:42 UTC</p>
      <p class="text-caption q-mb-xs">Asserted assertion ID: okta-saml-jane-abc123</p>
      <p class="text-caption q-mb-xs">Asserted security domain: https://acme.okta.com</p>
      <p class="text-caption q-mb-xs">Signature event observed by eSign Launchpad at: 2026-05-06 09:15:18 UTC</p>
      <p class="text-caption q-mb-none">Elapsed: 15 minutes after asserted authentication.</p>
    </q-card>

    <p class="q-mt-md">
      Two timestamps appear: the integrator-asserted authentication time
      (your clock) and the eSign Launchpad-observed signature event time
      (our clock). When the gap exceeds 30 minutes, the COC adds a callout
      noting that "a substantial period elapsed between the asserted
      authentication time and the signature event" - surfacing potential
      stale-session signings to a forensic reader.
    </p>

    <q-banner class="bg-grey-2 text-grey-9 q-mt-md" rounded>
      <strong>Why "asserted, not verified":</strong> eSign Launchpad never
      sees the SAML assertion itself - only your application does. We
      faithfully record what you tell us, but we cannot independently verify
      the claim. The COC wording reflects that honestly. If you require us
      to verify the SAML assertion server-side, contact us about full SSO
      integration (a separate feature).
    </q-banner>

    <h3>Step 3. Redirect the signer</h3>
    <p>
      Redirect the user's browser to the returned URL. The signer interacts
      with the signing ceremony as normal, reviewing documents, filling in
      fields, and submitting their signature. After completing (or declining),
      the signer sees a confirmation page on the eSign platform.
    </p>

    <q-banner class="bg-blue-1 text-blue-9 q-mt-md" rounded>
      <strong>Iframe vs redirect - pick the right pattern.</strong>
      The signing URL works in two ways: (a) <strong>Redirect</strong> the
      browser tab to it (whole-page takeover) - in this mode,
      <code>returnUrl</code> brings the user back to your app post-signing.
      (b) <strong>Embed</strong> it in an iframe via the SDK
      (<code>signing(...).mount('#container')</code>) - in this mode,
      the canonical pattern is to <strong>subscribe to <code>sessionEnd</code>,
      unmount the iframe, and render your own confirmation UI</strong>. Don't
      rely on the iframe's internal redirect to <code>returnUrl</code> for
      embedded signing - you have richer state in the parent app, and
      cross-protocol redirects (HTTPS parent &rarr; HTTP localhost during
      dev) are blocked by browser mixed-content policy and would orphan
      the iframe. Sample below.
    </q-banner>

    <GuideCodeTabs :samples="samples.embedSessionEndPattern" />


    <q-separator class="q-my-lg" />

    <h3>Tracking completion</h3>
    <p>
      After redirecting the signer, your application needs to know when they
      have finished. Use webhooks to receive real-time notification when the
      signer completes or declines the signing ceremony.
    </p>
    <p>
      Register a webhook subscription for the relevant events, such as
      <code>signer.completed</code>, <code>signer.declined</code>, or
      <code>package.completed</code>, and update your application state when
      those events arrive. Webhooks are the reliable, server-to-server way to
      track signing progress. See the
      <a href="/Docs/IntegrationGuides?topic=webhooks">Webhooks</a> guide for
      full details.
    </p>
    <p>
      As an alternative, you can poll the package status via
      <code>GET /api/v1/packages/{packageId}</code> to check if the signer's
      status has changed. Webhooks are strongly recommended for production
      integrations.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Security considerations</h3>
    <ul>
      <li>
        <strong>HMAC-signed tokens.</strong> Signing URLs contain an
        HMAC-signed token that is tamper-proof. Any modification to the URL
        parameters invalidates the token, and the signing ceremony will not
        load.
      </li>
      <li>
        <strong>Single-use and time-limited.</strong> Each signing URL is
        valid for a single session and expires after the configured duration.
        Default is 5 minutes, maximum is 60, configurable via
        <code>expiresInMinutes</code>.
      </li>
      <li>
        <strong>Server-side generation only.</strong> Always generate signing
        URLs from your backend server. Never expose the signing-URL generation
        endpoint directly to browsers, because it requires API authentication
        and the returned URL grants signing access.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>Browser refresh inside the iframe</h3>
    <p>
      Refresh is safe but lossy by design. Each refresh rehydrates the signer's
      session from our server, so any state the server has authoritatively
      recorded survives. State that exists only in the browser does not - that
      is intentional, not a gap. The signing record is built from server-side
      events, and we deliberately do not checkpoint mid-step UI state to
      browser storage; that would create a parallel evidence surface we do not
      want to defend in an audit.
    </p>
    <p><strong>What survives a refresh</strong></p>
    <ul>
      <li>Electronic-records consent acceptance</li>
      <li>Each completed authentication step (Access Code, SMS OTP, KBA pass, IDV pass)</li>
      <li>The adopted signature and initials images</li>
      <li>Every field already submitted as complete (signatures, text, dates, checkboxes, attachments)</li>
      <li>Auth attempt counters and lockout state</li>
    </ul>
    <p><strong>What does not survive a refresh</strong></p>
    <ul>
      <li>An in-progress KBA quiz. Refreshing while a quiz is on screen abandons that attempt; the next quiz request counts as a new attempt against the per-signer limit and incurs the standard KBA cost.</li>
      <li>An in-progress IDV (Persona) inquiry - the user is sent back to the IDV start screen.</li>
      <li>Drawn signature strokes that have not been adopted yet.</li>
      <li>Text typed into a field dialog that has not been confirmed.</li>
    </ul>

    <p><strong>Best practices for your parent application</strong></p>
    <ul>
      <li>
        <strong>Persist the signing URL across parent-page refreshes.</strong>
        A refresh of your application's page tears down the iframe entirely.
        Store the URL the moment your server hands it to the browser
        (<code>sessionStorage</code> is fine), and re-mount the SDK on app boot
        if the user returns to the same view:
<pre><code>// when you mint the URL
sessionStorage.setItem('esignlaunchpad.signingUrl', signingUrl);

// on app boot, if the user is mid-flow
const url = sessionStorage.getItem('esignlaunchpad.signingUrl');
if (url) signing(&#123; url, displayFormat: 'focused', autoResize: true &#125;).mount('#agreement');</code></pre>
        Clear the entry on <code>sessionEnd</code> so a fresh session does not
        rehydrate a stale URL.
      </li>
      <li>
        <strong>Or re-mint a fresh URL on resume.</strong> The signer's progress
        lives on the server; the URL is just an unexpired pass into the
        ceremony. Calling
        <code>POST /api/v1/packages/&#123;id&#125;/signers/&#123;id&#125;/signing-url</code>
        again is cheap and idempotent - the new token will land the user at
        whichever step they were on.
      </li>
      <li>
        <strong>Set caller expectations during high-cost steps.</strong> If your
        application surrounds the iframe with its own chrome (header, hamburger
        menu, navigation buttons), make sure those affordances do not silently
        navigate away from the page mid-signing. Inside the iframe we already
        prompt the browser's "leave this page?" warning during the KBA quiz;
        a parallel cue from your parent app is helpful.
      </li>
    </ul>

    <q-banner class="bg-blue-1 text-blue-9 q-mb-md" rounded>
      <template v-slot:avatar>
        <q-icon name="info" color="blue-9" />
      </template>
      <strong>Why we do not checkpoint mid-step state.</strong> Caching partial
      KBA answers, draft signatures, or unsubmitted field values to browser
      storage would let those values leak across sessions, devices, or users
      sharing a workstation, and would create disclosure obligations for data
      that is not part of the legal record. The signing record is exactly the
      events the server has acknowledged, nothing more. Refresh-as-clean-reset
      is the feature.
    </q-banner>

    <h3>Best practices</h3>
    <ul>
      <li>
        <strong>Use short expiration times.</strong> The default of 5 minutes
        is usually enough. Extend only when the signer's flow includes a
        deliberate wait, such as reviewing a document in a separate tab first.
        The maximum is 60 minutes.
      </li>
      <li>
        <strong>Generate a fresh URL each time.</strong> Do not cache or reuse
        signing URLs. Each time the signer needs to access the ceremony,
        generate a new URL from your server. That ensures the token is always
        valid and unexpired.
      </li>
      <li>
        <strong>Use webhooks for authoritative status.</strong> Webhooks
        confirm what the server recorded: signer completed, signer declined,
        package completed. Use webhooks as the source of truth for signing
        status in your application.
      </li>
      <li>
        <strong>Handle declines gracefully.</strong> A decline terminates the
        entire package. Provide your users with clear options to reassign the
        document to a different signer or void the package and start over.
      </li>
    </ul>

    <q-banner class="bg-blue-1 text-blue-9 q-mt-lg" rounded>
      <strong>Tip:</strong> You can mix embedded and email-based signers in
      the same package. For example, the employee signs via a URL generated
      by your HR app (embedded) while the manager receives an email to
      countersign at their convenience (email-based).
    </q-banner>

    <q-banner class="bg-green-1 text-green-9 q-mt-md" rounded>
      <template v-slot:avatar>
        <q-icon name="menu_book" color="green-9" />
      </template>
      See the <strong>Signers</strong> section in the
      <a href="/Docs/ApiReference" class="text-green-9" style="text-decoration: underline;">API Reference</a>
      for complete endpoint schemas and try-it-out examples.
    </q-banner>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const samples = {
    // ─── Create and send a package with an embedded signer (single call) ────────
    createAndSend: {
        curl: `curl -X POST https://api.esignlaunchpad.com/api/v1/packages/create-and-send/json \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Service Agreement",
    "signers": [
      {
        "email": "jane@example.com",
        "name": "Jane Doe",
        "clientUserId": "user-12345"
      }
    ],
    "documents": [
      {
        "fileName": "agreement.pdf",
        "documentBase64": "JVBERi0xLjQK..."
      }
    ]
  }'`,

        js: `const response = await fetch(
  'https://api.esignlaunchpad.com/api/v1/packages/create-and-send/json',
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      name: 'Service Agreement',
      signers: [{
        email: 'jane@example.com',
        name: 'Jane Doe',
        clientUserId: 'user-12345'
      }],
      documents: [{
        fileName: 'agreement.pdf',
        documentBase64: pdfBase64
      }]
    })
  }
);
const { packageId, signers } = await response.json();
const signerId = signers[0].id;`,

        node: `const response = await fetch(
  'https://api.esignlaunchpad.com/api/v1/packages/create-and-send/json',
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      name: 'Service Agreement',
      signers: [{
        email: 'jane@example.com',
        name: 'Jane Doe',
        clientUserId: 'user-12345'
      }],
      documents: [{
        fileName: 'agreement.pdf',
        documentBase64: pdfBase64
      }]
    })
  }
);
if (!response.ok) throw new Error(\`\${response.status} \${await response.text()}\`);
const { packageId, signers } = await response.json();
const signerId = signers[0].id;`,

        python: `import base64, os, requests

with open('agreement.pdf', 'rb') as f:
    pdf_b64 = base64.b64encode(f.read()).decode()

response = requests.post(
    'https://api.esignlaunchpad.com/api/v1/packages/create-and-send/json',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'name': 'Service Agreement',
        'signers': [{
            'email': 'jane@example.com',
            'name': 'Jane Doe',
            'clientUserId': 'user-12345'
        }],
        'documents': [{
            'fileName': 'agreement.pdf',
            'documentBase64': pdf_b64
        }]
    }
)
response.raise_for_status()
result = response.json()
package_id = result['packageId']
signer_id = result['signers'][0]['id']`,

        csharp: `var pdfBase64 = Convert.ToBase64String(await File.ReadAllBytesAsync("agreement.pdf"));

var payload = new
{
    name = "Service Agreement",
    signers = new[]
    {
        new
        {
            email = "jane@example.com",
            name = "Jane Doe",
            clientUserId = "user-12345"
        }
    },
    documents = new[]
    {
        new { fileName = "agreement.pdf", documentBase64 = pdfBase64 }
    }
};

var response = await client.PostAsJsonAsync(
    "api/v1/packages/create-and-send/json", payload);
response.EnsureSuccessStatusCode();
var result = await response.Content.ReadFromJsonAsync<JsonElement>();
var packageId = result.GetProperty("packageId").GetString();
var signerId = result.GetProperty("signers")[0].GetProperty("id").GetString();`,

        php: `$pdfBase64 = base64_encode(file_get_contents('agreement.pdf'));

$response = $client->post('api/v1/packages/create-and-send/json', [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'name' => 'Service Agreement',
        'signers' => [[
            'email' => 'jane@example.com',
            'name' => 'Jane Doe',
            'clientUserId' => 'user-12345',
        ]],
        'documents' => [[
            'fileName' => 'agreement.pdf',
            'documentBase64' => $pdfBase64,
        ]],
    ],
]);
$result = json_decode($response->getBody(), true);
$packageId = $result['packageId'];
$signerId = $result['signers'][0]['id'];`,

        ruby: `pdf_b64 = Base64.strict_encode64(File.binread('agreement.pdf'))

uri = URI('https://api.esignlaunchpad.com/api/v1/packages/create-and-send/json')
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  name: 'Service Agreement',
  signers: [{
    email: 'jane@example.com',
    name: 'Jane Doe',
    clientUserId: 'user-12345'
  }],
  documents: [{
    fileName: 'agreement.pdf',
    documentBase64: pdf_b64
  }]
}.to_json

res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
result = JSON.parse(res.body)
package_id = result['packageId']
signer_id = result['signers'][0]['id']`
    },

    // ─── Generate a signing URL ─────────────────────────────────────────────────
    getSigningUrl: {
        curl: `curl -X POST https://api.esignlaunchpad.com/api/v1/packages/$PACKAGE_ID/signers/$SIGNER_ID/signing-url \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "expiresInMinutes": 15,
    "returnUrl": "https://yourapp.com/contracts/42/thanks"
  }'`,

        js: `const response = await fetch(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}/signers/\${signerId}/signing-url\`,
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      expiresInMinutes: 15,
      returnUrl: 'https://yourapp.com/contracts/42/thanks'
    })
  }
);
const { url, expiresOn } = await response.json();
// Redirect or iframe the signer to the returned url - single-use and time-limited.`,

        node: `const response = await fetch(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}/signers/\${signerId}/signing-url\`,
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      expiresInMinutes: 15,
      returnUrl: 'https://yourapp.com/contracts/42/thanks'
    })
  }
);
if (!response.ok) throw new Error(\`\${response.status} \${await response.text()}\`);
const { url, expiresOn } = await response.json();`,

        python: `response = requests.post(
    f'https://api.esignlaunchpad.com/api/v1/packages/{package_id}/signers/{signer_id}/signing-url',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'expiresInMinutes': 15,
        'returnUrl': 'https://yourapp.com/contracts/42/thanks'
    }
)
response.raise_for_status()
url = response.json()['url']`,

        csharp: `var payload = new
{
    expiresInMinutes = 15,
    returnUrl = "https://yourapp.com/contracts/42/thanks"
};

var response = await client.PostAsJsonAsync(
    $"api/v1/packages/{packageId}/signers/{signerId}/signing-url", payload);
response.EnsureSuccessStatusCode();
var body = await response.Content.ReadFromJsonAsync<JsonElement>();
var url = body.GetProperty("url").GetString();`,

        php: `$response = $client->post(
    "api/v1/packages/{$packageId}/signers/{$signerId}/signing-url",
    [
        'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
        'json' => [
            'expiresInMinutes' => 15,
            'returnUrl' => 'https://yourapp.com/contracts/42/thanks',
        ],
    ]
);
$body = json_decode($response->getBody(), true);
$url = $body['url'];`,

        ruby: `uri = URI("https://api.esignlaunchpad.com/api/v1/packages/#{package_id}/signers/#{signer_id}/signing-url")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { expiresInMinutes: 15, returnUrl: 'https://yourapp.com/contracts/42/thanks' }.to_json

res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
url = JSON.parse(res.body)['url']`
    },

    // ─── Recommended embedded-signing client-side pattern ───────────────────────
    embedSessionEndPattern: {
        js: `// Recommended pattern for iframe-embedded signing: subscribe to
// sessionEnd, unmount the iframe, and render your own confirmation UI.
// Your parent app has richer state (current user, package context, next
// step in your workflow) and full control over the post-signing UX.

const handle = window.eSignLaunchpad
  .signing({ url: signingUrl })
  .on('ready', () => {
    console.log('Ceremony loaded; user can now review and sign.');
  })
  .on('sessionEnd', payload => {
    // payload = { event: 'signing_complete' | 'declined' | 'cancelled'
    //                  | 'session_timeout' | 'ttl_expired' | 'auth_failed'
    //                  | 'id_check_failed' | 'access_code_failed' | 'exception',
    //              signerId: '<guid>', reason?: '<detail>' }
    handle.unmount();

    if (payload.event === 'signing_complete') {
      // Update your app's state, navigate to the next step in your flow,
      // call your backend to refresh the package status, etc.
      router.push('/contracts/' + contractId + '/signed');
    } else if (payload.event === 'declined') {
      router.push('/contracts/' + contractId + '/declined');
    } else {
      // Show a recoverable error UI; let the user retry by re-minting a URL.
      showRetryDialog(payload);
    }
  })
  .on('error', err => {
    // SDK-side error (load timeout, postMessage validation failure, etc.)
    showRetryDialog({ event: 'exception', detail: err.message });
  })
  .mount('#agreement-container');`,
    },

    // ─── Signing-URL response body (single tab, JSON) ───────────────────────────
    signingUrlResponse: {
        json: `{
  "url": "https://esign.example.com/sign/abc123...",
  "expiresOn": "2026-03-01T15:15:00Z"
}`
    },

    // ─── Signing URL with externalAuthMetadata (Phase 3B, enterprise SSO use case) ─
    signingUrlWithExternalAuth: {
        curl: `curl -X POST https://api.esignlaunchpad.com/api/v1/packages/$PACKAGE_ID/signers/$SIGNER_ID/signing-url \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "expiresInMinutes": 30,
    "returnUrl": "https://yourapp.com/benefits/thanks",
    "allowedOrigins": ["https://yourapp.com"],
    "pingUrl": "https://yourapp.com/internal/keepalive",
    "pingFrequencySeconds": 120,
    "externalAuthMetadata": {
      "method": "SAML SSO via Okta",
      "instant": "2026-05-06T09:00:42Z",
      "assertionId": "okta-saml-jane-abc123",
      "securityDomain": "https://acme.okta.com"
    }
  }'`,

        js: `const response = await fetch(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}/signers/\${signerId}/signing-url\`,
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      expiresInMinutes: 30,
      returnUrl: 'https://yourapp.com/benefits/thanks',
      allowedOrigins: ['https://yourapp.com'],
      pingUrl: 'https://yourapp.com/internal/keepalive',
      pingFrequencySeconds: 120,
      externalAuthMetadata: {
        method: 'SAML SSO via Okta',
        instant: ssoSession.authenticatedAt.toISOString(),
        assertionId: ssoSession.assertionId,
        securityDomain: 'https://acme.okta.com'
      }
    })
  }
);
const { url, expiresOn } = await response.json();`,

        node: `const response = await fetch(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}/signers/\${signerId}/signing-url\`,
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      expiresInMinutes: 30,
      returnUrl: 'https://yourapp.com/benefits/thanks',
      allowedOrigins: ['https://yourapp.com'],
      pingUrl: 'https://yourapp.com/internal/keepalive',
      pingFrequencySeconds: 120,
      externalAuthMetadata: {
        method: 'SAML SSO via Okta',
        instant: ssoSession.authenticatedAt.toISOString(),
        assertionId: ssoSession.assertionId,
        securityDomain: 'https://acme.okta.com'
      }
    })
  }
);
if (!response.ok) throw new Error(\`\${response.status} \${await response.text()}\`);
const { url, expiresOn } = await response.json();`,

        python: `response = requests.post(
    f'https://api.esignlaunchpad.com/api/v1/packages/{package_id}/signers/{signer_id}/signing-url',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'expiresInMinutes': 30,
        'returnUrl': 'https://yourapp.com/benefits/thanks',
        'allowedOrigins': ['https://yourapp.com'],
        'pingUrl': 'https://yourapp.com/internal/keepalive',
        'pingFrequencySeconds': 120,
        'externalAuthMetadata': {
            'method': 'SAML SSO via Okta',
            'instant': sso_session.authenticated_at.isoformat() + 'Z',
            'assertionId': sso_session.assertion_id,
            'securityDomain': 'https://acme.okta.com',
        },
    }
)
response.raise_for_status()
url = response.json()['url']`,

        csharp: `var payload = new
{
    expiresInMinutes = 30,
    returnUrl = "https://yourapp.com/benefits/thanks",
    allowedOrigins = new[] { "https://yourapp.com" },
    pingUrl = "https://yourapp.com/internal/keepalive",
    pingFrequencySeconds = 120,
    externalAuthMetadata = new
    {
        method = "SAML SSO via Okta",
        instant = ssoSession.AuthenticatedAt.ToString("o"),
        assertionId = ssoSession.AssertionId,
        securityDomain = "https://acme.okta.com"
    }
};

var response = await client.PostAsJsonAsync(
    $"api/v1/packages/{packageId}/signers/{signerId}/signing-url", payload);
response.EnsureSuccessStatusCode();
var body = await response.Content.ReadFromJsonAsync<JsonElement>();
var url = body.GetProperty("url").GetString();`,

        php: `$response = $client->post(
    "api/v1/packages/{$packageId}/signers/{$signerId}/signing-url",
    [
        'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
        'json' => [
            'expiresInMinutes' => 30,
            'returnUrl' => 'https://yourapp.com/benefits/thanks',
            'allowedOrigins' => ['https://yourapp.com'],
            'pingUrl' => 'https://yourapp.com/internal/keepalive',
            'pingFrequencySeconds' => 120,
            'externalAuthMetadata' => [
                'method' => 'SAML SSO via Okta',
                'instant' => $ssoSession->authenticatedAt->format('c'),
                'assertionId' => $ssoSession->assertionId,
                'securityDomain' => 'https://acme.okta.com',
            ],
        ],
    ]
);
$body = json_decode($response->getBody(), true);
$url = $body['url'];`,

        ruby: `uri = URI("https://api.esignlaunchpad.com/api/v1/packages/#{package_id}/signers/#{signer_id}/signing-url")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  expiresInMinutes: 30,
  returnUrl: 'https://yourapp.com/benefits/thanks',
  allowedOrigins: ['https://yourapp.com'],
  pingUrl: 'https://yourapp.com/internal/keepalive',
  pingFrequencySeconds: 120,
  externalAuthMetadata: {
    method: 'SAML SSO via Okta',
    instant: sso_session.authenticated_at.iso8601,
    assertionId: sso_session.assertion_id,
    securityDomain: 'https://acme.okta.com'
  }
}.to_json

res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
url = JSON.parse(res.body)['url']`
    }
};

export default {
    name: 'GuideEmbeddedSigning',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>
